import { CheckOutlined, CopyOutlined, MailOutlined, MessageOutlined } from '@ant-design/icons';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { EMAILS } from '@app/constants';
import { AntdTypography, Button } from '@ui/components';
import { ContactSupportForm } from './components';
import { CONTACT_SUPPORT_KEY as KEY } from './constants';
import { useContactSupport } from './hooks';
import './styles.css';

const { Text } = AntdTypography;

type ContactSupportProps = {
  enabledAnalytics: boolean;
  searchString: string;
  title: string;
};

export const ContactSupport = ({ enabledAnalytics, searchString, title }: ContactSupportProps) => {
  const { t } = useTranslation();
  const {
    view,
    sentTo,
    isSubmitting,
    lastValues,
    handleOpen,
    handleRetry,
    handleCancel,
    handleSubmit,
    handleRecaptchaError,
    isEmailCopied,
    handleCopyEmail,
  } = useContactSupport(enabledAnalytics);

  return (
    <div className="rounded-2xl border border-solid border-primary-300 bg-primary-100 p-5 flex flex-col gap-4">
      {view === 'collapsed' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center">
              <MessageOutlined className="text-lg text-white" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Text className="text-base font-semibold text-base-800">{title}</Text>
              <Text className="text-sm text-base-700">{t(`${KEY}.description`)}</Text>
            </div>
          </div>
          <Button type="primary" size="large" onClick={handleOpen}>
            {t(`${KEY}.open`)}
          </Button>
        </div>
      )}

      {view === 'form' && (
        <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}>
          <ContactSupportForm
            isSubmitting={isSubmitting}
            initialValues={lastValues ?? { searchTerm: searchString }}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onRecaptchaError={handleRecaptchaError}
          />
        </GoogleReCaptchaProvider>
      )}

      {view === 'sent' && (
        <div className="flex items-start gap-3" role="status">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
            <CheckOutlined className="text-lg text-success" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Text className="text-base font-semibold text-base-800">{t(`${KEY}.sent_title`)}</Text>
            <Text className="text-sm text-base-700">
              {t(`${KEY}.sent_description`, { email: sentTo })}
            </Text>
          </div>
        </div>
      )}

      {view === 'error' && (
        <div className="flex flex-col gap-2 items-start" role="alert">
          <Text className="text-base font-semibold text-error">{t(`${KEY}.error_title`)}</Text>
          <Text className="text-sm text-base-700">{t(`${KEY}.error_description`)}</Text>
          <Button onClick={handleRetry}>{t(`${KEY}.retry`)}</Button>
        </div>
      )}

      <div className="border-0 border-t border-solid border-base-300 pt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <MailOutlined className="text-base-600" />
        <Text className="text-sm text-base-700">{t(`${KEY}.email_us`)}</Text>
        <a
          href={`mailto:${EMAILS.PATIENTS_CUREWIKI_HEALTH}`}
          className="text-sm font-semibold text-primary-600 hover:text-primary-700"
        >
          {EMAILS.PATIENTS_CUREWIKI_HEALTH}
        </a>
        <Button
          size="small"
          icon={isEmailCopied ? <CheckOutlined /> : <CopyOutlined />}
          aria-label={t(`${KEY}.copy_tooltip`)}
          onClick={handleCopyEmail}
        >
          {isEmailCopied ? t(`${KEY}.copied`) : t(`${KEY}.copy`)}
        </Button>
      </div>
    </div>
  );
};
